<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <span class="text-subtitle-1 font-weight-medium" v-html="title"></span>
      <v-spacer />
      <!-- Smart button -->
      <v-btn
        v-if="ctaRoute && ctaLabel"
        :to="ctaRoute"
        size="small"
        variant="tonal"
        color="primary"
        class="mr-2"
        :prepend-icon="ctaIconResolved"
      >
        {{ ctaLabel }}
      </v-btn>
      <span class="legend-dot ml-2" :style="{ backgroundColor: indicatorColor }"></span>
    </v-card-title>

    <v-data-table
      density="compact"
      :headers="headers"
      :items="items"
      class="elevation-2"
      hide-default-footer
    >
      <template #item="{ item, columns }">
        <tr
          :class="{ 'row-hot': isRowHot(item.raw ?? item) }"
          @mouseenter="
            $emit('row-enter', { id: (item.raw ?? item)[idKey], type, item: item.raw ?? item })
          "
          @mouseleave="
            $emit('row-leave', { id: (item.raw ?? item)[idKey], type, item: item.raw ?? item })
          "
          @click="
            $emit('row-click', { id: (item.raw ?? item)[idKey], type, item: item.raw ?? item })
          "
          style="cursor: pointer"
        >
          <td v-for="col in columns" :key="col.key">
            <!-- unwrap once -->
            <template v-if="col.key === 'assigned_to'">
              <v-avatar size="34" class="mr-2 bg-avatar">
                <v-img :src="getUserIconUrl((item.raw ?? item).assignedToIcon)" />
              </v-avatar>
              {{ (item.raw ?? item).assignedToFK }}
            </template>

            <template v-else-if="col.key === 'ownerFK'">
              <v-avatar size="34" class="mr-2 bg-avatar">
                <v-img :src="getUserIconUrl((item.raw ?? item).ownerIconId)" />
              </v-avatar>
              {{ (item.raw ?? item).ownerFK }}
            </template>

            <template v-else-if="col.key === 'dueDate'">
              <span class="cell-pill" :class="{ overdue: isPastDate((item.raw ?? item).dueDate) }">
                {{ formatDate((item.raw ?? item).dueDate) }}
              </span>
            </template>

            <template v-else-if="col.key === 'submitTime'">
              <span class="cell-pill">
                {{ formatDate((item.raw ?? item).submitTime) }}
              </span>
            </template>

            <template v-else-if="col.key === 'priorityFK'">
              <v-chip
                :color="(item.raw ?? item).priorityColour"
                class="ma-2"
                small
                variant="elevated"
                text-color="white"
                style="width: 100%; text-align: center; margin: 0 auto"
              >
                <span>{{ (item.raw ?? item).priorityFK }}</span>
              </v-chip>
            </template>

            <!-- DEFAULT: read from raw by key, then fallback to Vuetify's col.value -->
            <template v-else>
              {{ (item.raw ?? item)[col.key] ?? col.value ?? "—" }}
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
export default {
  name: "DashboardTaskTable",
  props: {
    title: { type: String, default: "" },
    headers: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    type: { type: String, default: "task" }, // 'task' | 'finding' | 'inspection'
    idKey: { type: String, default: "id" },
    externalHoverId: { type: [String, Number, null], default: null },
    externalHoverIds: { type: Array, default: () => [] },
    ctaTo: { type: [String, Object], default: null }, // e.g. "/tasks" or { name: 'Tasks' }
    ctaText: { type: String, default: "" }, // e.g. "View All"
    ctaIcon: { type: String, default: "" }, // e.g. "mdi-format-list-bulleted"
    useDefaultCta: { type: Boolean, default: true },
  },
  computed: {
    // Hardwired mapping: title keyword → color
    indicatorColor() {
      const t = (this.title || "").toLowerCase();
      if (t.includes("task")) return "#2a72cf"; // tasks = blue
      if (t.includes("finding")) return "#f2994a"; // findings = orange
      if (t.includes("inspection")) return "none"; // inspections = green
      return "#c0c5d0"; // default grey
    },
    ctaRoute() {
      if (this.ctaTo) return this.ctaTo;
      if (!this.useDefaultCta) return null;
      const map = { task: "/tasks", finding: "/findings", inspection: "/inspections" };
      return map[this.type] || null;
    },
    ctaLabel() {
      if (this.ctaText) return this.ctaText;
      if (!this.useDefaultCta) return null;
      const map = {
        task: "All Tasks",
        finding: "All Findings",
        inspection: "All Inspections",
      };
      return map[this.type] || null;
    },
    ctaIconResolved() {
      if (this.ctaIcon) return this.ctaIcon;
      const map = {
        task: "mdi-format-list-bulleted",
        finding: "mdi-alert-circle-outline",
        inspection: "mdi-clipboard-text-outline",
      };
      return map[this.type] || "mdi-chevron-right";
    },
    hoverIdSet() {
      // normalize to a Set of strings for robust compare
      const arr = Array.isArray(this.externalHoverIds) ? this.externalHoverIds : [];
      return new Set(arr.map((v) => String(v)));
    },
  },
  methods: {
    isPastDate(v) {
      const d = this.parseDate(v);
      if (!d) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    },
    parseDate(v) {
      if (!v) return null;
      if (v instanceof Date && !isNaN(v)) return v;

      if (typeof v === "string") {
        // ISO-ish
        if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
          const d = new Date(v);
          return isNaN(d) ? null : d;
        }
        // dd/MM/yyyy or dd/MM/yy
        const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
        if (m) {
          let [, dd, mm, yy] = m;
          let y = parseInt(yy, 10);
          if (yy.length === 2) y += y >= 70 ? 1900 : 2000;
          const d = new Date(y, parseInt(mm, 10) - 1, parseInt(dd, 10));
          return isNaN(d) ? null : d;
        }
      }
      return null;
    },
    formatDate(v) {
      if (!v) return "—";

      // If it's already a Date
      if (v instanceof Date && !isNaN(v)) {
        return v.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }

      // If it's ISO-ish
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        const d = new Date(v);
        if (!isNaN(d)) {
          return d.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
      }

      // If it's dd/MM/yyyy or dd/MM/yy
      if (typeof v === "string") {
        const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
        if (m) {
          let [_, dd, mm, yy] = m;
          const day = parseInt(dd, 10);
          const mon = parseInt(mm, 10) - 1;
          let yr = parseInt(yy, 10);
          if (yy.length === 2) yr += yr >= 70 ? 1900 : 2000; // naive pivot
          const d = new Date(yr, mon, day);
          if (!isNaN(d)) {
            return d.toLocaleDateString(undefined, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            });
          }
        }
      }

      // Fallback: show raw
      return String(v);
    },

    PriorityColor(priority) {
      switch (priority?.toLowerCase()) {
        case "high":
          return "black ";
        case "medium":
          return "orange";
        case "low":
          return "green";
        default:
          return "grey";
      }
    },
    getUserIconUrl(id) {
      // default to 1 if invalid, pad to two digits
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    rowClass(row) {
      // Vuetify passes a wrapper; unwrap safely
      const raw = row?.raw ?? row?.item ?? row;
      const id = raw?.[this.idKey];
      return id === this.externalHoverId ? "row-hot" : "";
    },
    isRowHot(row) {
      const id = row?.[this.idKey];
      if (id == null) return false;
      if (this.externalHoverId != null && String(this.externalHoverId) === String(id)) return true;
      return this.hoverIdSet.has(String(id));
    },
    itemRowProps(row) {
      // unwrap Vuetify's wrapper
      const raw = row?.raw ?? row?.item ?? row;
      const id = raw?.[this.idKey];

      return {
        class: id === this.externalHoverId ? "row-hot" : "",
        onMouseenter: () => this.$emit("row-enter", { id, type: this.type }),
        onMouseleave: () => this.$emit("row-leave", { id, type: this.type }),
        onClick: () => this.$emit("row-click", { id, type: this.type }),
        style: "cursor:pointer",
      };
    },
  },
};
</script>

<style>
thead {
  font-weight: 500;
  color: #99a1af !important;
}
.v-chip__content {
  align-items: center;
  margin: 0 auto;
}
.v-table .v-table__wrapper > table > tbody > tr > td {
  border-bottom: none !important;
}

/* the tiny circle 123 */
.legend-dot {
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: inline-block;
  vertical-align: middle;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.15) inset, 0 0 0 1px rgba(0, 0, 0, 0.06);
}

/* full-width pill inside a cell so background fills the cell visually */
.cell-pill {
  display: block;
  width: 100%;
  text-align: center;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
  color: #666;
}
.cell-pill.overdue {
  background: #d32f2f; /* red 700-ish */
  color: #fff;
}

:deep(.row-hot) {
  background: rgba(42, 114, 207, 0.1);
  border-radius: 6px;
}
:deep(.v-table .v-table__wrapper > table > tbody > tr.row-hot > td) {
  background-color: rgba(42, 114, 207, 0.12) !important;
  transition: background-color 0.15s ease;
}

/* optional subtle hover for any row */
:deep(.v-table .v-table__wrapper > table > tbody > tr:hover > td) {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
